import { view } from "@risingstack/react-easy-state";
import { Accordion, Grid } from "@tine/designsystem-ui-react";

import isEmpty from "lodash/isEmpty";

import type { Product } from "../../common/types/productTypes";

import { translateNutritionBaseUnit } from "../../common/utils";
import ProductInfo from "./ProductInfo";

import { Interweave } from "interweave";
import { useEffect, useRef, useState } from "react";
import { sendAccordionClick, sendButtonClick } from "../../common/tracking";
import NutritionWrapper from "./NutritionWrapper";
import { AllergensDisplay } from "./ProductDetailsPage";

type ProductDescriptionAndContentProps = {
   product: Product;
};

const ProductDescriptionAndContent = ({ product }: ProductDescriptionAndContentProps) => {
   const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
   const [isTruncatable, setIsTruncatable] = useState(false);
   const descriptionRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const el = descriptionRef.current;
      if (el) {
         const isClamped = el.scrollHeight > el.clientHeight + 1;
         setIsTruncatable(isClamped);
      }
   }, [product.description]);

   const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      const button = event.target as HTMLButtonElement;
      setIsDescriptionExpanded((prev) => !prev);
      if (button.textContent) {
         sendButtonClick(button.textContent);
      }
   };

   return (
      <>
         <Grid cols={12} gap={2}>
            {!isEmpty(product.description) && (
               <Grid.Col span={{ base: 12, lg: 4 }} className="tw-mr-4 md:tw-p-0 tw-pb-6">
                  <h3 className="tw-text-base">Beskrivelse</h3>
                  <div className="tw-relative">
                     <div
                        ref={descriptionRef}
                        className={`tw-transition-all ${isDescriptionExpanded ? "" : "tw-line-clamp-4 tw-overflow-hidden"}`}
                     >
                        <p className="tw-m-0 tw-text-sm">
                           <Interweave content={product.description?.toString() || ""} />
                        </p>
                     </div>

                     {isTruncatable && (
                        <button
                           type="button"
                           onClick={(button) => handleClick(button)}
                           className="tw-mt-2 tw-font-semibold hover:tw-underline tw-text-sm"
                        >
                           {isDescriptionExpanded ? "Les mindre" : "Les mer"}
                        </button>
                     )}
                  </div>
               </Grid.Col>
            )}
            <Grid.Col span={{ base: 12, lg: 8 }}>
               {!!product.ingredients && (
                  <Accordion
                     label="Ingredienser"
                     variant="compact"
                     className="tw-max-w-[1000px]"
                     iconPosition="right"
                     onClick={() => sendAccordionClick("Ingredienser")}
                  >
                     <p>
                        <Interweave content={product.ingredients?.toString() || ""} />
                     </p>
                  </Accordion>
               )}
               <Accordion
                  label="Produktinformasjon"
                  variant="compact"
                  className="tw-max-w-[1000px]"
                  iconPosition="right"
                  onClick={() => sendAccordionClick("Produktinformasjon")}
               >
                  <ProductInfo product={product} />
               </Accordion>
               {!isEmpty(product?.allergens) && (
                  <Accordion
                     label="Allergener"
                     variant="compact"
                     className="tw-max-w-[1000px]"
                     iconPosition="right"
                     onClick={() => sendAccordionClick("Allergener")}
                  >
                     <AllergensDisplay product={product} />
                  </Accordion>
               )}

               {product.nutritions && product.nutritions.length > 0 && (
                  <Accordion
                     label={`Næringsinnhold ${translateNutritionBaseUnit(product.nutritionBaseUnit)}`}
                     variant="compact"
                     className="tw-max-w-[1000px] !tw-border-ink-border-subtle"
                     iconPosition="right"
                     onClick={() => sendAccordionClick("Næringsinnhold")}
                  >
                     <NutritionWrapper nutrition={product.nutritions} />
                  </Accordion>
               )}
            </Grid.Col>
         </Grid>
      </>
   );
};

export default view(ProductDescriptionAndContent);
